import {
  IRynReplayBatchLimits,
  IRynReplaySessionLifecycleConfig,
  RYN_REPLAY_FULL_SNAPSHOT_EVENT_TYPE,
} from '@ups/xplat/base/ryn-replay';

/** Minimal structural shape of rrweb's `record`, so tests need no rrweb. */
export type RynReplayRecordFn = (options: {
  emit: (event: unknown) => void;
  maskAllInputs?: boolean;
  /**
   * CSS selector for elements whose text is masked. `'*'` masks every text
   * node on the page (PR 3639 review's mask-all-text option), never set
   * unless the per-app policy's `maskAllText` opts in.
   */
  maskTextSelector?: string;
  /** CSS class whose text is masked on any app (WI 34326, `ryn-replay-mask`). */
  maskTextClass?: string;
  blockClass?: string;
  /**
   * CSS selector for elements blocked outright (whole subtree, no recording),
   * combined with `blockClass` by OR, never a replacement for it (rrweb's
   * `isBlocked()` checks both). Used for third-party components whose
   * container never carries `ryn-replay-block` but still renders PII via an
   * attribute (e.g. Kendo's `kendo-numerictextbox` mirrors its value onto
   * `aria-valuenow`, which `maskTextSelector`/`maskAllInputs` cannot reach).
   */
  blockSelector?: string;
  recordCanvas?: boolean;
  collectFonts?: boolean;
  inlineStylesheet?: boolean;
  /**
   * Drop script/comment/head-meta noise from snapshots (#31398 ladder step 2).
   * `'all'` is rrweb's built-in slim preset; it shrinks FullSnapshot work without
   * blanking application UI the way `blockClass` does.
   */
  slimDOMOptions?: 'all' | true;
}) => (() => void) | undefined;

/**
 * rrweb `EventType` values, mapped to the envelope's `type` string. Kept as an
 * explicit table rather than an import so a rrweb major version cannot silently
 * renumber what lands in the index, and because `type` is length- and
 * charset-validated server-side (1-60 of `[A-Za-z0-9._-]`).
 *
 * Type 2's name is the shared keyframe token rather than a literal: the
 * platform-neutral retry-queue, batch-fit and offline-store rules all refuse to
 * discard a batch carrying that exact string, so spelling it independently here
 * is how this table and those rules would drift apart.
 */
export const RYN_REPLAY_EVENT_TYPE_NAMES: Record<number, string> = {
  0: 'DomContentLoaded',
  1: 'Load',
  2: RYN_REPLAY_FULL_SNAPSHOT_EVENT_TYPE,
  3: 'IncrementalSnapshot',
  4: 'Meta',
  5: 'Custom',
  6: 'Plugin',
};

export const RYN_REPLAY_UNKNOWN_EVENT_TYPE = 'Unknown';

export interface IRynReplayRrwebBackendConfig {
  app: string;
  platform?: string;
  limits?: Partial<IRynReplayBatchLimits>;
  flushIntervalMs?: number;
  recordCanvas?: boolean;
  /** Used so network capture does not stamp ingest POSTs with a user-session trace. */
  ingestUrl?: string;
  /**
   * Absolute UPS API bases that may receive a correlation `traceparent`.
   * Third-party hosts are never stamped (CORS preflight blast radius).
   */
  correlationAllowedApiBaseUrls?: readonly string[];
  sessionLifecycle?: Partial<IRynReplaySessionLifecycleConfig>;
}
